import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";

import { apiClient } from "../../lib/apiClient";
import { ApiRequestError } from "../../lib/errors";
import { queryKeys } from "../../lib/queryKeys";
import type { ResolvedCellSupplierMap } from "../variables/ResolvedCellView";

interface VariableSupplierIds {
  personaId: string | null;
  scenarioId: string | null;
}

// Supplier names are read live (`display_name`); frozen revisions are not
// surfaced by the Transcript variables API.
export function useVariableSuppliers(
  { personaId, scenarioId }: VariableSupplierIds,
  enabled: boolean,
): ResolvedCellSupplierMap {
  const persona = useQuery({
    enabled: enabled && personaId !== null,
    queryKey: queryKeys.personas.detail(personaId ?? ""),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/personas/{id}", {
        params: { path: { id: personaId ?? "" } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
  });
  const scenario = useQuery({
    enabled: enabled && scenarioId !== null,
    queryKey: queryKeys.scenarios.detail(scenarioId ?? ""),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/scenarios/{id}", {
        params: { path: { id: scenarioId ?? "" } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
  });

  return useMemo(
    () => ({
      ...(persona.data === undefined
        ? {}
        : {
            persona: {
              id: persona.data.id,
              name: persona.data.display_name,
            },
          }),
      ...(scenario.data === undefined
        ? {}
        : {
            scenario: {
              id: scenario.data.id,
              name: scenario.data.display_name,
            },
          }),
    }),
    [persona.data, scenario.data],
  );
}
